import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Heart, Users, Shield, Calendar, Stethoscope, Baby, Activity, Phone, Globe, Clock, ExternalLink } from 'lucide-react';
import ScheduleModal from '../components/ScheduleModal';

const Home = () => {
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const services = [
    {
      icon: <Stethoscope className="w-10 h-10" />,
      title: 'Internal Medicine',
      description: 'Comprehensive adult primary care in DeLand and Orange City for patients 18 and older.',
      link: '/services'
    },
    {
      icon: <Baby className="w-10 h-10" />,
      title: 'Pediatric Care',
      description: 'Expert care for newborns through adolescents by our pediatric specialists.',
      link: '/services'
    },
    {
      icon: <Activity className="w-10 h-10" />,
      title: 'Telehealth Services',
      description: 'Virtual pediatric and primary care consultations for DeLand and Orange City patients.',
      link: '/services'
    },
    {
      icon: <Heart className="w-10 h-10" />,
      title: 'Chronic Disease Management',
      description: 'Ongoing care for diabetes, hypertension, asthma, and more in West Volusia.',
      link: '/services'
    },
  ];

  const providers = [
    {
      name: 'Dr. Concepcion Anayas, M.D.',
      specialty: 'Pediatrics',
      ageGroup: 'Newborn - Adolescent',
      image: 'https://customer-assets.emergentagent.com/job_new-cmcwv/artifacts/7gjbdm6j_Dr%20C%20AI.jpg',
    },
    {
      name: 'Dr. Marcelo Anayas, M.D.',
      specialty: 'Internal Medicine',
      ageGroup: 'Adults (18+)',
      image: 'https://customer-assets.emergentagent.com/job_new-cmcwv/artifacts/k257skf3_Dr%20M.jpg',
    },
    {
      name: 'Pam Perry, ARNP',
      specialty: 'Pediatrics',
      ageGroup: 'Newborn - Adolescent',
      image: 'https://customer-assets.emergentagent.com/job_new-cmcwv/artifacts/a2wxig6b_Pam%20AI%20Final.jpg',
    },
  ];

  return (
    <div className="min-h-screen">
      {/* ====== HERO SECTION ====== */}
      <section className="relative bg-gradient-to-br from-blue-600 via-blue-700 to-teal-600 text-white pt-36 pb-24 lg:pt-40 lg:pb-32 px-4 overflow-hidden">
        <div className="absolute inset-0 opacity-15">
          <img 
            src="https://images.unsplash.com/photo-1631217868264-e5b90bb7e133?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHw0fHxkb2N0b3IlMjBwYXRpZW50fGVufDB8fHx8MTc2MDU4NzU1M3ww&ixlib=rb-4.1.0&q=85" 
            alt="Pediatrician examining child patient in DeLand Florida" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="space-y-8">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight tracking-tight">
                Pediatrics & Internal Medicine in DeLand and Orange City
              </h1>
              <p className="text-xl lg:text-2xl text-blue-100 leading-relaxed font-medium">
                Trusted healthcare for your entire family. Expert pediatric care from Dr. Concepcion Anayas, M.D. and Pam Perry, ARNP. Adult primary care from Dr. Marcelo Anayas, M.D.
              </p>
              <div className="flex flex-wrap gap-3 text-base lg:text-lg font-semibold">
                <span className="bg-white/20 px-4 py-2 rounded-full">Rural Health Clinic</span>
                <span className="bg-white/20 px-4 py-2 rounded-full">Medicaid Accepted</span>
                <span className="bg-white/20 px-4 py-2 rounded-full">Walk-ins Welcome</span>
                <span className="bg-white/20 px-4 py-2 rounded-full flex items-center gap-2">
                  <Globe size={18} />
                  Se Habla Español
                </span>
              </div>
              {/* STANDARDIZED CTA: "Schedule" / "Call" */}
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <button
                  onClick={() => setShowScheduleModal(true)}
                  data-testid="hero-schedule-btn"
                  className="inline-flex items-center justify-center bg-white text-blue-700 px-8 py-5 rounded-xl font-bold text-lg shadow-xl hover:bg-blue-50 transition-all transform hover:scale-105 cursor-pointer"
                >
                  <Calendar className="mr-3" size={24} />
                  Schedule Appointment
                </button>
                <a
                  href="tel:386-775-1792"
                  data-testid="hero-call-btn"
                  className="inline-flex items-center justify-center bg-blue-800 border-2 border-white text-white px-8 py-5 rounded-xl font-bold text-lg hover:bg-blue-900 transition-all"
                >
                  <Phone className="mr-3" size={24} />
                  Call (386) 775-1792
                </a>
              </div>
            </div>
            
            {/* Hero Info Card */}
            <div className="hidden lg:block">
              <div className="bg-white rounded-2xl p-8 shadow-2xl">
                <h2 className="text-2xl font-bold text-gray-900 mb-8">Why Choose Our Clinic?</h2>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="bg-blue-100 text-blue-700 p-3 rounded-xl flex-shrink-0">
                      <Shield size={28} />
                    </div>
                    <div className="text-gray-700">
                      <p className="font-bold text-lg text-gray-900">Rural Health Clinic</p>
                      <p>Serving underserved communities in DeLand and Orange City</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="bg-blue-100 text-blue-700 p-3 rounded-xl flex-shrink-0">
                      <Users size={28} />
                    </div>
                    <div className="text-gray-700">
                      <p className="font-bold text-lg text-gray-900">Medicaid Accepted</p>
                      <p>Sunshine, Humana, FL Community Care</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="bg-teal-100 text-teal-700 p-3 rounded-xl flex-shrink-0">
                      <Globe size={28} />
                    </div>
                    <div className="text-gray-700">
                      <p className="font-bold text-lg text-gray-900">Bilingual Staff</p>
                      <p>Se Habla Español - Spanish-speaking staff available</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="bg-blue-100 text-blue-700 p-3 rounded-xl flex-shrink-0">
                      <Clock size={28} />
                    </div>
                    <div className="text-gray-700">
                      <p className="font-bold text-lg text-gray-900">Saturday Hours</p>
                      <p>8:00 AM - 1:00 PM (call to verify)</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ====== WELCOME SECTION (White bg) ====== */}
      <section className="py-20 lg:py-28 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="order-2 lg:order-1">
              <img 
                src="https://images.pexels.com/photos/4021775/pexels-photo-4021775.jpeg" 
                alt="Family receiving pediatric care at Community Medical Center in West Volusia Florida" 
                className="rounded-2xl shadow-2xl w-full"
              />
            </div>
            <div className="order-1 lg:order-2 space-y-6">
              <h2 className="text-3xl lg:text-4xl font-extrabold text-gray-900 leading-tight">
                Your Trusted Healthcare Provider in West Volusia
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed">
                Looking for a <strong className="text-blue-700">pediatrician in DeLand</strong> or <strong className="text-blue-700">pediatrician in Orange City</strong>? Community Medical Center of West Volusia provides quality healthcare for families throughout Volusia County.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                <Link to="/providers" className="text-blue-700 font-semibold hover:underline">Dr. Concepcion Anayas, M.D. and Pam Perry, ARNP</Link> specialize in pediatric care from newborns through adolescents, while <Link to="/providers" className="text-blue-700 font-semibold hover:underline">Dr. Marcelo Anayas, M.D.</Link> provides internal medicine for adults.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                As a <strong>Rural Health Clinic</strong>, we serve <strong>DeLand, Orange City, Deltona, and all of West Volusia</strong>. Our bilingual staff ensures Spanish-speaking patients receive excellent care.
              </p>
              
              {/* Insurance Box */}
              <div className="bg-blue-50 rounded-xl p-6 border-l-4 border-blue-600">
                <h3 className="font-bold text-gray-900 mb-4 text-lg">We Accept Medicaid HMO Plans:</h3>
                <ul className="grid grid-cols-2 gap-3 text-gray-700">
                  <li className="flex items-center gap-2"><div className="w-2 h-2 bg-blue-600 rounded-full"></div> Sunshine Health</li>
                  <li className="flex items-center gap-2"><div className="w-2 h-2 bg-blue-600 rounded-full"></div> Humana</li>
                  <li className="flex items-center gap-2"><div className="w-2 h-2 bg-blue-600 rounded-full"></div> Florida Community Care</li>
                  <li className="flex items-center gap-2"><div className="w-2 h-2 bg-blue-600 rounded-full"></div> UnitedHealthcare – Effective 5/1/2026</li>
                </ul>
              </div>

              {/* Patient Portal */}
              <div className="bg-gradient-to-r from-blue-700 to-blue-800 rounded-xl p-6 text-white">
                <h3 className="font-bold text-lg mb-2">Patient Portal &amp; Bill Pay</h3>
                <p className="text-blue-200 text-sm mb-4">Access your health records, view test results, and pay your bill online through our secure Healow patient portal.</p>
                <a
                  href="https://mycw249.ecwcloud.com/portal30300/jsp/login.jsp"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="home-patient-portal-btn"
                  className="inline-flex items-center gap-2 bg-white text-blue-700 px-5 py-3 rounded-lg font-bold hover:bg-blue-50 transition-colors"
                >
                  <ExternalLink size={18} />
                  Patient Portal &amp; Bill Pay
                </a>
              </div>
              
              <Link
                to="/about"
                className="inline-flex items-center text-blue-700 font-bold text-lg hover:text-blue-800 transition-colors"
              >
                Learn More About Our Clinic
                <ArrowRight className="ml-2" size={22} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ====== SERVICES SECTION (Gray bg for separation) ====== */}
      <section className="py-20 lg:py-28 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-extrabold text-gray-900 mb-4">
              Pediatrics & Internal Medicine Services
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Comprehensive healthcare for children and adults in DeLand and Orange City, Florida
            </p>
          </div>
          
          {/* CONSISTENT HEIGHT CARDS */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {services.map((service, index) => (
              <Link
                to={service.link}
                key={index}
                data-testid={`service-card-${index}`}
                className="bg-white rounded-xl p-6 lg:p-8 hover:shadow-2xl transition-all transform hover:-translate-y-2 border border-gray-100 group flex flex-col h-full"
              >
                <div className="text-blue-600 mb-5 bg-blue-50 w-16 h-16 lg:w-20 lg:h-20 rounded-xl flex items-center justify-center group-hover:bg-blue-100 transition-colors flex-shrink-0">
                  {service.icon}
                </div>
                <h3 className="text-lg lg:text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                <p className="text-gray-600 leading-relaxed flex-grow">{service.description}</p>
              </Link>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <Link
              to="/services"
              className="inline-flex items-center text-blue-700 font-bold text-lg hover:text-blue-800 transition-colors"
            >
              View All Services
              <ArrowRight className="ml-2" size={22} />
            </Link>
          </div>
        </div>
      </section>

      {/* ====== PROVIDERS SECTION (White bg for contrast) ====== */}
      <section className="py-20 lg:py-28 px-4 bg-white">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-3xl lg:text-4xl font-extrabold text-gray-900 mb-4">
            Meet Our Providers
          </h2>
          <p className="text-xl text-gray-600 mb-16 max-w-2xl mx-auto">
            Experienced physicians and nurse practitioners dedicated to your family's health in West Volusia
          </p>
          
          {/* CONSISTENT HEIGHT PROVIDER CARDS */}
          <div className="grid sm:grid-cols-3 gap-8 lg:gap-12 max-w-5xl mx-auto mb-12">
            {providers.map((provider, index) => (
              <Link 
                key={index} 
                to="/providers" 
                className="group flex flex-col h-full"
              >
                <div className="hover:shadow-xl transition-all hover:-translate-y-2 flex flex-col items-center h-full">
                  {/* Provider Photo - CLEAN, NO FRAME */}
                  <div className="w-40 h-40 lg:w-48 lg:h-48 mb-5 rounded-2xl overflow-hidden shadow-xl flex-shrink-0">
                    <img 
                      src={provider.image}
                      alt={`${provider.name} - ${provider.specialty} in DeLand and Orange City FL`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="text-center">
                    <h3 className="font-bold text-gray-900 text-lg">{provider.name}</h3>
                    <p className="text-blue-700 font-semibold">{provider.specialty}</p>
                    <p className="text-sm text-gray-500 mt-1">{provider.ageGroup}</p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
          
          {/* STANDARDIZED CTA: "Meet Our Providers" */}
          <Link
            to="/providers"
            data-testid="meet-providers-btn"
            className="inline-flex items-center bg-blue-700 text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-blue-800 transition-colors shadow-lg"
          >
            Meet Our Providers
            <ArrowRight className="ml-2" size={22} />
          </Link>
        </div>
      </section>

      {/* ====== BILINGUAL BANNER (Teal accent for visual break) ====== */}
      <section className="py-12 lg:py-14 px-4 bg-teal-600 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex items-center justify-center gap-4 mb-4">
            <Globe size={36} />
            <h2 className="text-2xl lg:text-3xl font-bold">Se Habla Español</h2>
          </div>
          <p className="text-lg text-teal-100">
            Our clinic has bilingual staff members who speak Spanish to better serve our diverse West Volusia community.
          </p>
        </div>
      </section>

      {/* ====== FINAL CTA SECTION (Dark blue for strong finish) ====== */}
      <section className="py-20 lg:py-28 px-4 bg-gradient-to-br from-blue-700 to-blue-900 text-white">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-3xl lg:text-4xl font-extrabold mb-6">
            Ready to Schedule Your Appointment?
          </h2>
          <p className="text-xl text-blue-200 mb-12">
            Two convenient locations in DeLand and Orange City, Florida. Walk-ins welcome!
          </p>
          
          {/* Location Cards - CONSISTENT HEIGHT */}
          <div className="grid sm:grid-cols-2 gap-6 mb-12">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 text-left flex flex-col h-full">
              <h3 className="font-bold text-2xl mb-3">Orange City Office</h3>
              <p className="text-blue-200 mb-4 text-lg flex-grow">810 Commed Blvd.<br />Orange City, FL 32763</p>
              <a href="tel:386-775-1792" className="text-white font-bold text-2xl hover:text-blue-200 transition-colors">(386) 775-1792</a>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 text-left flex flex-col h-full">
              <h3 className="font-bold text-2xl mb-3">DeLand Office</h3>
              <p className="text-blue-200 mb-4 text-lg flex-grow">1190 N. Stone St.<br />DeLand, FL 32720</p>
              <a href="tel:386-738-1792" className="text-white font-bold text-2xl hover:text-blue-200 transition-colors">(386) 738-1792</a>
            </div>
          </div>
          
          {/* STANDARDIZED CTA: "Schedule" / "Contact" */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => setShowScheduleModal(true)}
              data-testid="cta-schedule-online-btn"
              className="inline-flex items-center justify-center bg-white text-blue-700 px-10 py-5 rounded-xl font-bold text-lg shadow-xl hover:bg-blue-50 transition-all cursor-pointer"
            >
              <Calendar className="mr-3" size={24} />
              Schedule Online
            </button>
            <Link
              to="/contact"
              data-testid="cta-contact-btn"
              className="inline-flex items-center justify-center bg-blue-800 border-2 border-white text-white px-10 py-5 rounded-xl font-bold text-lg hover:bg-blue-900 transition-all"
            >
              Contact Us
              <ArrowRight className="ml-3" size={24} />
            </Link>
          </div>
        </div>
      </section>

      <ScheduleModal isOpen={showScheduleModal} onClose={() => setShowScheduleModal(false)} />
    </div>
  );
};

export default Home;
